import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as Yup from 'yup';

import { ModalHeader } from '../../components/ModalHeader';
import { TrainingParamsFields } from '../../components/TrainingParamsFields';
import { useToast } from '../../hooks/toast';

import { api } from '../../services/api';
import { formatSplitLabel } from '../../utils/formatSplitLabel';
import { getValidationErrors } from '../../utils/getValidationErrors';
import { getExercisePrimaryMedia } from '../../utils/getExercisePrimaryMedia';
import { IExerciseMedia } from '../../domains/Exercise';

import {
  Cancel,
  Container,
  Content,
  FooterRow,
  FormContainer,
  Submit,
} from './styles';
import { useModal } from '../../hooks/modal';

interface IRequestTraining {
  exercise: {
    name: string;
    muscle_group: string;
    media?: IExerciseMedia[];
  };
  user: {
    avatar_url: string | undefined;
  };
  id: string;
  interval_min: number | undefined;
  interval_max: number | undefined;
  reps_min: number | undefined;
  reps_max: number | undefined;
  sets_min: number | undefined;
  sets_max: number | undefined;
  additional_information: string | undefined;
  split_index: number;
}

interface IChangeUserFormProp {
  interval_min: number;
  interval_max: number;
  reps_min: number;
  reps_max: number;
  sets_min: number;
  sets_max: number;
  additional_information: string;
  type: string;
}

interface IChangeTrainingProps {
  training_id: string;
}

const ChangeTrainingUser: React.FC<IChangeTrainingProps> = ({
  training_id,
}) => {
  const formRef = useRef<FormHandles>(null);
  const [training, setTraining] = useState<IRequestTraining>();
  const { addToast } = useToast();
  const { removeModal } = useModal();

  const handleSubmit = useCallback(
    async (data: IChangeUserFormProp): Promise<void> => {
      try {
        formRef.current?.setErrors({});

        // `originalValue` é o valor cru do form (sempre string, mesmo
        // vazia, porque input HTML). Checar `Number.isNaN` só no valor já
        // castado (como era antes) não pega string vazia -- `Number.isNaN('')`
        // é `false`, então `''` passava direto e o backend rejeitava com
        // "must be a number". Aqui checa o valor original antes do cast.
        const numberTransform = (
          currentValue: number,
          originalValue: unknown,
        ): number | undefined => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }

          return Number.isNaN(currentValue) ? undefined : currentValue;
        };

        const schema = Yup.object().shape({
          reps_min: Yup.number().nullable(true).transform(numberTransform),
          reps_max: Yup.number().nullable(true).transform(numberTransform),
          sets_max: Yup.number().nullable(true).transform(numberTransform),
          sets_min: Yup.number().nullable(true).transform(numberTransform),
          interval_min: Yup.number().nullable(true).transform(numberTransform),
          interval_max: Yup.number().nullable(true).transform(numberTransform),
          additional_information: Yup.string(),
        });

        const validatedData = await schema.validate(data, {
          abortEarly: false,
        });

        const dataUpdateTraining = {
          interval_min: validatedData.interval_min,
          interval_max: validatedData.interval_max,
          sets_min: validatedData.sets_min,
          sets_max: validatedData.sets_max,
          reps_min: validatedData.reps_min,
          reps_max: validatedData.reps_max,
          additional_information: data.additional_information?.length
            ? data.additional_information
            : undefined,
        };

        await api.put(`trainings/${training_id}`, dataUpdateTraining);

        addToast({
          title: 'Dados alterados com sucesso',
          type: 'success',
        });

        removeModal();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          title: 'Erro na atualização',
          description:
            'Houve um erro na atualização dos dados. Tente novamente.',
          type: 'error',
        });
      }
    },
    [addToast, training_id],
  );

  useEffect(() => {
    api
      .get(`trainings/${training_id}`)
      .then(response => {
        setTraining(response.data);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos dados do treino.',
          description: err?.response?.status,
        });
      });
  }, []);

  return (
    <Container>
      <ModalHeader
        title={training?.exercise.name || ''}
        subtitle={training?.exercise.muscle_group || ''}
        avatarUrl={
          getExercisePrimaryMedia(training?.exercise.media)?.thumbnail_url
        }
        badge={formatSplitLabel(training?.split_index).title}
      />

      <Content>
        <FormContainer
          ref={formRef}
          initialData={training}
          onSubmit={handleSubmit}
        >
          <TrainingParamsFields />

          <FooterRow>
            <Cancel type="button" onClick={() => removeModal()}>
              Cancelar
            </Cancel>
            <Submit name="submit" value="Alterar exercício" />
          </FooterRow>
        </FormContainer>
      </Content>
    </Container>
  );
};

export { ChangeTrainingUser };
